import React, { memo, useMemo } from "react";
import { Button, Icon, IconButton } from "@mui/material";

import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import { isParticipantChannelSelectionEnabled } from "@/features/campaigns/shared/helpers/channelSelectionHelper";
import { getFormatDate } from "@/helpers/utils";
import { CurrencyFormateText } from "@/components/CurrencyFormat";
import RpImageTextInline from "@/components/RpImageTextInline";
import { CAMPAIGN_CONST } from "@/config/constants";
import RpSvgIcon from "@/components/icons/RpSvgIcon";

type CampaignCreative = {
  creative_id?: string | number;
  creative_name?: string;
};

type ConfirmationChannel = {
  channel_id?: string | number;
  channel_name?: string | number;
  is_selected?: boolean;
};

type ConfirmationLocation = {
  locationId?: string | number;
  locationName?: string;
  start_date?: string;
  end_date?: string;
  budget?: string | number;
  brand_contribution_amount?: string | number;
  creativeData?: CampaignCreative[];
  channelData?: ConfirmationChannel[];
  enabledChannels?: Array<string | number>;
  channels?: Array<string | number>;
};

type ConfirmationCampaign = {
  budget_type?: string;
  can_participant_select_specific_channels?: unknown;
  brand_contribution?: unknown;
  contribution_percentage?: string | number;
  contribution_max_limit?: string | number;
  brand_contribution_type?: string;
};

type ConfirmationJoinCampaignCardProps = {
  location: ConfirmationLocation;
  campaign?: ConfirmationCampaign;
  onDeleteLocation: (
    _locationId?: ConfirmationLocation["locationId"]
  ) => React.MouseEventHandler<HTMLButtonElement> | undefined;
  onEditSelections?: (
    _location: ConfirmationLocation
  ) => React.MouseEventHandler<HTMLButtonElement> | undefined;
};

export default memo(function ConfirmJoinCampaignCard({
  location,
  campaign,
  onDeleteLocation,
  onEditSelections,
}: ConfirmationJoinCampaignCardProps) {
  const { creativeData = [], locationName, start_date, end_date } = location;
  const canDeleteLocation =
    campaign?.budget_type === CAMPAIGN_CONST.BUDGET_TYPE.LOCATION_SPECIFIC;
  const showSelectedChannels = isParticipantChannelSelectionEnabled(campaign);
  const selectedChannelNames = useMemo(() => {
    const channelData = Array.isArray(location?.channelData)
      ? location.channelData
      : [];

    const selectedChannels = channelData
      .filter((channel) => channel.is_selected)
      .map((channel) => channel.channel_name || channel.channel_id)
      .filter(Boolean);

    if (selectedChannels.length) {
      return [...new Set(selectedChannels)];
    }

    return (location?.enabledChannels || location?.channels || [])
      .map((channel) => String(channel))
      .filter(Boolean);
  }, [location]);

  const withBrandContributionAmount = useMemo(() => {
    const { budget, brand_contribution_amount } = location;

    return (
      parseFloat(String(budget)) +
      joinCampaignHelper.getBrandContributionAmount({
        amount: parseFloat(String(budget)),
        brand_contribution: campaign?.brand_contribution,
        contribution_percentage: campaign?.contribution_percentage,
        contribution_max_limit: campaign?.contribution_max_limit,
        brand_contribution_type: campaign?.brand_contribution_type,
        brand_match_preset_amount: Number(brand_contribution_amount),
      })
    );
  }, [location, campaign]);

  return (
    <div className="card h-100 confirm-join-campaign-card">
      <div className="card-body">
        <div
          className={`row confirmation-selection-grid ${
            canDeleteLocation ? "confirmation-selection-grid-with-delete" : ""
          } ${
            showSelectedChannels
              ? ""
              : "confirmation-selection-grid-without-channels"
          }`}
        >
          <div className="col-lg-3 mb-2 confirmation-selection-item confirmation-selection-location">
            <RpImageTextInline
              alignCenter={false}
              svgIcon="location_mark"
              imageProps={{ color: "primary" }}
              text={
                <>
                  <div className="title d-none d-lg-block">Location</div>
                  <div className="title-val">{locationName}</div>
                  <Button
                    variant="text"
                    className="edit-selections link fs-12 mt-2 text-underline"
                    onClick={onEditSelections?.(location)}
                    size="small"
                  >
                    Edit Selections
                  </Button>
                </>
              }
            />
          </div>
          <div className="col-6 col-lg-2 mb-2 confirmation-selection-item confirmation-selection-offer">
            <RpImageTextInline
              alignCenter={false}
              svgIcon="campaign_calender"
              imageProps={{ color: "primary" }}
              text={
                <>
                  <div className="title">Selected Offer</div>
                  <div className="title-val">
                    {/* {creativeData.map((c) => c.creative_name).join(", ")} */}
                    <ul className="pl-0 mb-0 list-s-none">
                      {creativeData.map((c) => (
                        <li key={c.creative_id}>{c.creative_name}</li>
                      ))}
                    </ul>
                  </div>
                </>
              }
            />
          </div>
          <div className="col-6 col-lg-2 mb-2 confirmation-selection-item confirmation-selection-start-date">
            <RpImageTextInline
              alignCenter={false}
              svgIcon="campaign_calender"
              imageProps={{ color: "primary" }}
              text={
                <>
                  <div className="title">Start Date</div>
                  <div className="title-val">
                    {getFormatDate(start_date, "MMM Do, YYYY")}
                  </div>
                </>
              }
            />
          </div>
          <div className="col-6 col-lg-1 mb-2 confirmation-selection-item confirmation-selection-end-date">
            <RpImageTextInline
              alignCenter={false}
              svgIcon="campaign_calender"
              imageProps={{ color: "primary" }}
              text={
                <>
                  <div className="title">End Date</div>
                  <div className="title-val">
                    {getFormatDate(end_date, "MMM Do, YYYY")}
                  </div>
                </>
              }
            />
          </div>
          {showSelectedChannels && (
            <div className="col-6 col-lg-2 mb-2 confirmation-selection-item confirmation-selection-channels">
              <RpImageTextInline
                alignCenter={false}
                svgIcon="campaign_calender"
                imageProps={{ color: "primary" }}
                text={
                  <>
                    <div className="title">Selected Channels</div>
                    <div className="title-val">
                      {selectedChannelNames.length
                        ? selectedChannelNames.join(", ")
                        : "Not selected"}
                    </div>
                  </>
                }
              />
            </div>
          )}
          <div className="col-6 col-lg-2 mb-2 text-lg-right d-lg-none">
            <RpImageTextInline
              alignCenter={false}
              svgIcon="money"
              imageProps={{ color: "primary" }}
              text={
                <>
                  <div className="title">Budget</div>
                  <div className="title-val">
                    <CurrencyFormateText
                      value={withBrandContributionAmount}
                      suffix=" USD"
                    />
                  </div>
                </>
              }
            />
          </div>
          <div className="col-6 col-lg mb-2 d-none d-lg-block confirmation-selection-item confirmation-selection-budget">
            <div>
              <div className="title">
                <Icon
                  component={RpSvgIcon}
                  name="money"
                  color="primary"
                  fontSize="small"
                  className="mr-1"
                />
                Budget
              </div>
              <div className="title-val">
                <CurrencyFormateText
                  value={withBrandContributionAmount}
                  suffix=" USD"
                />
              </div>
            </div>
          </div>
          {canDeleteLocation && (
            <div className="col-lg-1 text-right confirmation-selection-item confirmation-selection-delete">
              <IconButton onClick={onDeleteLocation(location?.locationId)}>
                <Icon src="/img/icons/trash.svg" component="img" />
              </IconButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
